// conversationAdapter/citations — folds the conversation's out-of-band
// `citation_marker` artifacts into per-message källhänvisningar the chat
// render (cite-B) will consume.
//
// This is the CLIENT WIRE SHAPE for anchored source citations. A
// `SourceCited` domain event folds into `ActiveConversation.artifacts` as
// a `CitationMarker`; `showConversation` serialises the whole conversation
// (artifacts included), so each marker arrives here verbatim as wire JSON.
// This module decodes ONE marker into a `MessageCitation` — the flat,
// render-ready value the `sketches/cite-inline/CiteInline.tsx` +
// `ReferencesPanel.tsx` `Citation` view-model is built from.
//
// FOLD ONLY — no rendering. Deliberately NOT pre-formatted:
//   - `locator` stays STRUCTURED (`page` / `heading` / `element`), not the
//     Swedish string "s. 12" the sketch shows. Formatting "s. N" and
//     resolving the deep-link `onOpen`/`onOpenSource` callbacks are the
//     render's job (i18n + navigation live there), so cite-B maps this
//     `MessageCitation` → the sketch's `Citation` by formatting `locator`,
//     carrying `quote` verbatim, and wiring the callbacks off `resourceId`.
//   - `index` (the footnote number) is positional per message — assigned
//     by the render when it lays the citations out, not stored here.
//
// Grounded in the REAL wire shapes (packages/core/src/anchor.ts): a
// `Target`'s `selectors` is a `List.NonEmpty` (`{_tag, head, tail}`), each
// selector a tagged variant; a `TextQuoteSelector` carries the verbatim
// `exact`. Decoded total: any shape mismatch degrades to "absent", never a
// throw (same posture as the rest of the adapter).

export type { CitationLocator };
export { toMessageCitations };

import { fields, list, num, str } from "../wireShape.js";

// The specific position a källhänvisning points at — never the whole
// file (owner refinement: a citation always names a part). One of the
// three structural addressing schemes `Anchor.PathSegment` carries. The
// render turns this into a human locator ("s. 12", a heading, a block).
type CitationLocator =
    | { readonly type: "page"; readonly page: number }
    | { readonly type: "heading"; readonly slug: string }
    | { readonly type: "element"; readonly elementId: string };

// One cited part of one source, ready for cite-B to render. `messageId`
// threads it back into the owning agent bubble; `resourceId` is the
// deep-link target; `sourceTitle` is the cite-time display snapshot (still
// present even if the source was later deleted). `locator` is the specific
// position; `quote` the optional verbatim excerpt (present only when the
// anchor carried a `TextQuoteSelector`).
type MessageCitation = {
    readonly id: string;
    readonly messageId: string;
    readonly resourceId: string;
    readonly sourceTitle: string;
    readonly locator: CitationLocator | undefined;
    readonly quote: string | undefined;
};

// Flatten a `List.NonEmpty` wire object (`{_tag, head, tail}`) into a
// plain array. A non-list-shaped value yields `[]`.
const nonEmptyToArray = (v: unknown): readonly unknown[] => {
    const f = fields(v);
    if (f === undefined) return [];
    if (!f.has("head")) return [];
    return [f.get("head"), ...list(f.get("tail"))];
};

// The first specific position across a Target's selectors, if any. Scans
// the structural selector's path segments for a page / heading / element.
const locatorOf = (
    selectors: readonly unknown[],
): CitationLocator | undefined => {
    for (const sel of selectors) {
        const s = fields(sel);
        if (str(s?.get("type")) !== "structural_selector") continue;
        for (const seg of nonEmptyToArray(s?.get("path"))) {
            const g = fields(seg);
            const type = str(g?.get("type"));
            if (type === "page_segment") {
                const page = num(g?.get("page"));
                if (page !== undefined) return { type: "page", page };
            } else if (type === "heading_segment") {
                const slug = str(g?.get("slug"));
                if (slug !== undefined) return { type: "heading", slug };
            } else if (type === "element_segment") {
                const elementId = str(g?.get("elementId"));
                if (elementId !== undefined)
                    return { type: "element", elementId };
            }
        }
    }
    return undefined;
};

// The verbatim excerpt from the first `TextQuoteSelector`, if any.
const quoteOf = (selectors: readonly unknown[]): string | undefined => {
    for (const sel of selectors) {
        const s = fields(sel);
        if (str(s?.get("type")) !== "text_quote_selector") continue;
        const exact = str(s?.get("exact"));
        if (exact !== undefined) return exact;
    }
    return undefined;
};

// Decode one `citation_marker` artifact into a `MessageCitation`. Returns
// `undefined` when the marker lacks the fields a citation needs (a
// messageId + a source ref + at least a title), so a malformed marker
// drops out rather than rendering a blank reference.
const toMessageCitation = (
    artifact: ReadonlyMap<string, unknown> | undefined,
    ordinal: number,
): MessageCitation | undefined => {
    const messageId = str(artifact?.get("messageId"));
    const citation = fields(artifact?.get("citation"));
    const sourceTitle = str(citation?.get("sourceTitle"));
    const resourceRef = fields(citation?.get("resourceRef"));
    const resourceId = str(resourceRef?.get("id"));
    if (
        messageId === undefined ||
        sourceTitle === undefined ||
        resourceId === undefined
    ) {
        return undefined;
    }
    const target = fields(citation?.get("target"));
    const selectors = nonEmptyToArray(target?.get("selectors"));
    return {
        id: `${messageId}-cite-${ordinal}`,
        messageId,
        resourceId,
        sourceTitle,
        locator: locatorOf(selectors),
        quote: quoteOf(selectors),
    };
};

// Fold every källhänvisning in the conversation, oldest first — the same
// `showConversation` payload `toChatEntries` reads. cite-B groups them by
// `messageId` for the inline markers and lists them all in the
// ReferencesPanel.
const toMessageCitations = (value: unknown): readonly MessageCitation[] => {
    const conversation = fields(fields(value)?.get("conversation"));
    const active = conversation?.has("entries")
        ? conversation
        : fields(conversation?.get("prior"));
    const out: MessageCitation[] = [];
    let ordinal = 0;
    for (const raw of list(active?.get("artifacts"))) {
        const artifact = fields(raw);
        if (str(artifact?.get("type")) !== "citation_marker") continue;
        const citation = toMessageCitation(artifact, ordinal);
        if (citation !== undefined) {
            out.push(citation);
            ordinal += 1;
        }
    }
    return out;
};
